import type { FC, FormEvent } from 'react';
import React, { memo, useEffect, useRef, useState } from 'react';

import { Box, Collapse, useMediaQuery, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router';

import { NetworkRoutesPathEnum } from '@setup/router/routes/network/networkRoutesPath.enum';
import { PlusIcon, SearchIcon } from '@setup/svg-icons/svg-icons';
import { FiltersUpdateArea } from '@shared/enums/filtersUpdateArea.enum';
import useAnalyticsEventTracker from '@shared/hooks/analytics/useAnalyticsEventTracker';
import { useAppDispatch } from '@shared/hooks/redux';
import { useDebounce } from '@shared/hooks/useDebounce';
import HubFilterPopover from '@shared/ui/popovers/HubFilterPopover';
import { updateFilters } from '@store/Common/commonThunk';
import { CreateActionMenu, MuiBaseTextFiled, MuiButton, SelectRole } from 'shared/ui-kit';

import { NetworkNavigationPanelContainer } from './NetworkNavigationPanel.style';

import type { RenderNetworkAddItemMenuPropsType } from '@pages/networking/Network/components/network/functions/renderNetworkAddItemMenu';
import type { ActionMenuListModel } from '@shared/models/actionMenuList.model';
import type { ConnectionsFiltersType, ContactsFiltersType } from '@shared/models/network';
import type { HubFiltersType } from '@shared/ui/popovers/HubFilterPopover/types';

interface Props {
  setFilters: (inputValue: string, linking: HubFiltersType, role: string | undefined) => void;
  filters: ContactsFiltersType | ConnectionsFiltersType;
  isContact?: boolean;
  actionButtonConfig: {
    onClick?: () => void;
    menuList?: (props: RenderNetworkAddItemMenuPropsType) => ActionMenuListModel;
  };
}

const NetworkNavigationPanel: FC<Props> = ({ setFilters, filters, actionButtonConfig, isContact }) => {
  const theme = useTheme();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const { t } = useTranslation();
  const isMobileDisplay = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.down(900));

  const gaEventTracker = useAnalyticsEventTracker();

  const isSentPage =
    location.pathname === `${NetworkRoutesPathEnum.network}/${NetworkRoutesPathEnum.sent_requests}`;

  const [query, setQuery] = useState<string>(filters?.query ?? '');
  const [linkingValue, setLinkingValue] = useState<HubFiltersType>(filters?.linking ? filters.linking : null);
  const [roleValue, setRoleValue] = useState<string | undefined>(filters?.role ? filters.role : '');

  const inputRef = useRef<HTMLInputElement>(null);

  const [isShowSearch, setIsShowSearch] = useState<boolean>(false);

  const debounceSearch = useDebounce((inputValue: string) => {
    setFilters(inputValue, linkingValue, roleValue);
  }, 1500);

  const updateFilter = ({ linking, role }: { linking: HubFiltersType; role: string | undefined }) => {
    const newConnectionsFilters = {
      role: role,
      linking: linking,
    };

    dispatch(
      updateFilters({
        filters: newConnectionsFilters,
        area: isContact ? FiltersUpdateArea.contact : FiltersUpdateArea.connection,
      }),
    ).then((res) => {
      if (updateFilters.fulfilled.match(res)) {
        setFilters(query, linking, role);
      }
    });
  };

  const handleChangeTags = (linking: HubFiltersType) => {
    setLinkingValue(linking);
    updateFilter({ linking, role: roleValue });
  };

  const handleChangeSearch = (inputValue: string) => {
    setQuery(inputValue);
    debounceSearch(inputValue);
  };

  const handleChangeRole = (value: string) => {
    setRoleValue(value);
    updateFilter({ linking: linkingValue, role: value });
  };

  useEffect(() => {
    const hasQuery = !!filters.query;
    setQuery(filters.query ?? '');
    setLinkingValue(filters.linking);
    setRoleValue(filters.role);

    if (!hasQuery) setIsShowSearch(false);
  }, [filters.query, filters.linking, filters.role]);

  const handleChangeIsShowSearch = (val: boolean) => {
    setIsShowSearch(val);
  };

  const handleSearchButtonClick = () => {
    handleChangeIsShowSearch(true);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  return (
    <NetworkNavigationPanelContainer>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', width: '100%' }}>
        <Box display={'flex'}>
          <Box
            sx={{
              maxWidth: '260px',
              minWidth: '160px',
            }}
            mr="16px"
          >
            <HubFilterPopover
              isFullWidthChildrenContainer={true}
              selectedItems={linkingValue}
              placeholder={'Area'}
              callback={(e) => {
                handleChangeTags(e);
              }}
            />
          </Box>
          <Box
            sx={{
              maxWidth: '260px',
              minWidth: '160px',
            }}
            mr="16px"
          >
            <SelectRole
              isCreatable
              value={roleValue ?? ''}
              placeholder={'Role'}
              onChange={(role) => {
                handleChangeRole(role);
              }}
            />
          </Box>
        </Box>

        <Box display={'flex'} alignItems={'center'}>
          <Collapse in={isShowSearch && !isTablet} unmountOnExit orientation={'horizontal'}>
            <Box>
              <MuiBaseTextFiled
                variant="outlined"
                required
                value={query}
                placeholder={t('general.placeholders.search')}
                ref={inputRef}
                size="medium"
                inputVariant={'search'}
                onChange={(e: FormEvent<HTMLInputElement>) => handleChangeSearch(e.currentTarget.value)}
                onBlur={() => (!!query ? true : handleChangeIsShowSearch(false))}
                onClearValue={() => handleChangeIsShowSearch(false)}
                isClearable
                inputProps={{
                  startAdornment: <SearchIcon />,
                }}
              />
            </Box>
          </Collapse>

          <Collapse in={!isShowSearch} orientation={'horizontal'} unmountOnExit easing={'in'}>
            <Box
              sx={{
                display: 'flex',
              }}
            >
              <MuiButton
                size="medium"
                label=""
                isDisabled={false}
                startIcon={<SearchIcon />}
                variant="outlined"
                color="secondary"
                onClick={handleSearchButtonClick}
              />
            </Box>
          </Collapse>

          <Box sx={{ ml: '16px' }}>
            {actionButtonConfig.onClick ? (
              <MuiButton
                isStopPropagation={false}
                onClick={actionButtonConfig.onClick}
                label={t('general.buttons.add')}
                variant="contained"
                size="medium"
                startIcon={<PlusIcon />}
              />
            ) : (
              actionButtonConfig.menuList && (
                <CreateActionMenu
                  childrenComponent={
                    <MuiButton
                      isStopPropagation={false}
                      label={t('general.buttons.add')}
                      variant="contained"
                      size="medium"
                      startIcon={<PlusIcon />}
                    />
                  }
                  menuList={actionButtonConfig.menuList({
                    isMobileDisplay,
                    theme,
                    t,
                    dispatch,
                    canAddUserOnPage: isSentPage,
                    gaEventTracker,
                  })}
                  header={null}
                  isMobile={false}
                  isMobileVariant={false}
                  anchorOriginHorizontal="right"
                  anchorOriginVertical="bottom"
                  transformOriginHorizontal="left"
                  transformOriginVertical="top"
                />
              )
            )}
          </Box>
        </Box>
      </Box>
      <Collapse in={isShowSearch && isTablet} unmountOnExit>
        <Box mt={'12px'}>
          <MuiBaseTextFiled
            variant="outlined"
            required
            value={query}
            placeholder={t('general.placeholders.search')}
            ref={inputRef}
            size="medium"
            inputVariant={'search'}
            onChange={(e: FormEvent<HTMLInputElement>) => handleChangeSearch(e.currentTarget.value)}
            onBlur={() => (!!query ? true : handleChangeIsShowSearch(false))}
            onClearValue={() => handleChangeIsShowSearch(false)}
            isClearable
            inputProps={{
              startAdornment: <SearchIcon />,
            }}
          />
        </Box>
      </Collapse>
    </NetworkNavigationPanelContainer>
  );
};

export default memo(NetworkNavigationPanel);
